<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import type { ProjectRegistrationSumApiResponse } from './project.types';
import { useRegistrationsQuery } from './registrations';
import api from '/@utilities/http';
import { date } from '/@utilities/intl';

const { projectId } = defineProps<{ projectId: number }>();

const totalHoursQuery = useQuery<ProjectRegistrationSumApiResponse>({
  queryFn: () =>
    api.get(`/projects/${projectId}/registrations/sum`).then((response) => response.data),
  queryKey: ['registrations-sum', { projectId }],
});

const { registrations, isLoading, fetchNextPage, isFetchingNextPage, dateIntervalText } =
  useRegistrationsQuery(projectId);
</script>

<template>
  <section class="project-booking">
    <h3>
      Timer registrert totalt:
      <DfLoading v-if="totalHoursQuery.isFetching.value" :no-float="true" />
      <template v-else>{{ totalHoursQuery.data.value?.TotalHours }}</template>
    </h3>

    <table class="card">
      <thead>
        <tr>
          <th>Navn</th>
          <th>Dato</th>
          <th>Timer</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="{ id, userName, hours, date: registrationDate } in registrations" :key="id">
          <td>{{ userName }}</td>
          <td>{{ date(new Date(registrationDate), { weekday: false }) }}</td>
          <td>{{ hours }}</td>
        </tr>
      </tbody>

      <tfoot>
        <tr v-if="registrations?.length === 0">
          <td colspan="3" class="center-content">Ingen timer i denne perioden</td>
        </tr>
        <tr>
          <td colspan="3">
            <div class="load-more-button-section">
              <p>Viser fra {{ dateIntervalText }}</p>

              <df-button v-on:click="fetchNextPage()" :disabled="isFetchingNextPage || isLoading">
                Hent eldre
                <template v-if="isFetchingNextPage" v-slot:icon><DfLoading no-float /></template>
              </df-button>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  </section>
</template>

<style scoped>
table {
  width: 100%;
  text-align: left;
  border-collapse: collapse;
  font-size: 0.9rem;

  & th {
    font-size: 0.8rem;
    font-weight: normal;
  }

  & th,
  & td {
    padding: var(--gap-sm) var(--gap-md);
  }
}

tr:not(tfoot tr) {
  border-bottom: 1px solid var(--color-divider);
}

.project-booking {
  display: grid;
  gap: var(--gap-md);
}

.center-content {
  text-align: center;
}

.load-more-button-section {
  display: flex;
  flex-direction: row;
  width: 100%;

  align-items: center;
  justify-content: space-between;
}
</style>
